import { Box } from '@mui/material';
import type { DataQuality } from '@/lib/dataQuality';
import type { KlasyfikacjaZrodla } from '@/components/SzufladaDowodu';

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * JĘZYK WIZUALNY JAKOŚCI DANYCH (warstwa wspólna nr 6)
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * DWA OSOBNE URZĄDZENIA, NIE JEDNO — i to jest tu najważniejsza decyzja.
 *
 *   MIERNIK       mówi, ILE WARTA JEST PRÓBA: cztery słupki rosnącej wysokości, skala porządkowa
 *                 0–4 przeniesiona z makiety. Odpowiada na pytanie „czy na tym można oprzeć wniosek".
 *
 *   ZNACZNIK      mówi, SKĄD SĄ DANE I Z KIEDY: kategoria, nie stopień. Odpowiada na pytanie
 *                 „czym to w ogóle jest".
 *
 * Zlanie ich w jeden wskaźnik byłoby najprostszą drogą do liczby w rodzaju „jakość danych: 87%",
 * która brzmi precyzyjnie i nie znaczy nic. Świeżość źródła, wielkość próby, klasyfikacja
 * pochodzenia i wiarygodność złączenia to CZTERY różne pytania; średnia z nich nie odpowiada
 * na żadne. Dlatego skale są rozdzielone typem, a nie tylko opisem.
 *
 * KOLOR NIGDY NIE JEST JEDYNYM NOŚNIKIEM: miernik niesie informację WYSOKOŚCIĄ słupków, znacznik
 * ma tekst, a oba mają pełną nazwę dostępną.
 */

/* ─── MIERNIK PRÓBY ──────────────────────────────────────────────────────────────────────── */

const OPIS_POZIOMU: Record<number, string> = {
  0: 'Brak danych — nie ma czego oceniać',
  1: 'Bardzo słabe — próba za mała na wniosek',
  2: 'Słabe — istotne braki',
  3: 'Dobre — drobne braki',
  4: 'Pełne — bez zastrzeżeń',
};

/** Odwzorowanie istniejącej oceny jakości na skalę makiety. Jawne, żeby dało się je sprawdzić. */
export function poziomZJakosci(q: DataQuality): number {
  switch (q) {
    case 'GOOD':
      return 4;
    case 'PARTIAL':
      return 3;
    case 'LOW_SAMPLE':
      return 2;
    case 'MISSING_HISTORY':
      return 1;
    // UNRELIABLE_JOIN i UNKNOWN to NIE jest „słaba próba" — to brak podstawy do jakiejkolwiek
    // oceny. Dlatego zero, a nie jeden: pusty miernik ma wyglądać na pusty.
    default:
      return 0;
  }
}

export function Miernik({ poziom, etykieta }: { poziom: number; etykieta?: string }) {
  const p = Math.max(0, Math.min(4, Math.round(poziom)));
  const opis = `Jakość danych: ${OPIS_POZIOMU[p]}`;
  const barwa = p >= 4 ? 'var(--sukces)' : p >= 3 ? 'var(--seria-1)' : p >= 2 ? 'var(--uwaga)' : 'var(--linia-mocna)';

  return (
    <Box component="span" className="miernik" role="img" aria-label={etykieta ? `${etykieta}. ${opis}` : opis} title={opis}>
      {[1, 2, 3, 4].map((i) => (
        <Box component="i" key={i} sx={{ background: i <= p ? barwa : 'var(--linia-mocna)' }} />
      ))}
    </Box>
  );
}

/* ─── ZNACZNIK POCHODZENIA I ŚWIEŻOŚCI ───────────────────────────────────────────────────── */

export type StanZrodla =
  | 'BIEZACE'
  | 'NIEAKTUALNE'
  | 'NIEUSTALONE'
  | 'ZABLOKOWANE'
  | 'KONTROLOWANE'
  | 'REFERENCYJNE_TEST'
  | 'ZA_MALO_DANYCH';

const ZNACZNIK: Record<StanZrodla, { tekst: string; barwa: string; tlo: string; opis: string }> = {
  BIEZACE: {
    tekst: 'bieżące',
    barwa: 'var(--sukces)',
    tlo: 'var(--sukces-tlo)',
    opis: 'Źródło odczytane niedawno, a jego zawartość pochodzi z dnia odczytu.',
  },
  NIEAKTUALNE: {
    tekst: 'nieaktualne',
    barwa: 'var(--uwaga)',
    tlo: 'var(--uwaga-tlo)',
    opis: 'Odczyt jest zaległy albo źródło oddało zawartość z wcześniejszego dnia.',
  },
  NIEUSTALONE: {
    tekst: 'świeżość nieustalona',
    barwa: 'var(--neutralny)',
    tlo: 'var(--neutralny-tlo)',
    opis: 'Nie wiadomo, z którego momentu pochodzi zawartość źródła. To NIE znaczy, że jest stara — znaczy, że nie wiemy.',
  },
  ZABLOKOWANE: {
    tekst: 'zablokowane',
    barwa: 'var(--krytyczny)',
    tlo: 'var(--krytyczny-tlo)',
    opis: 'Odczytu nie da się wykonać — przyczyna opisana przy źródle.',
  },
  KONTROLOWANE: {
    tekst: 'zbiór kontrolowany',
    barwa: 'var(--seria-2)',
    tlo: 'var(--neutralny-tlo)',
    opis: 'Prawdziwe API i schemat, dane wprowadzone na potrzeby weryfikacji. Nie wolno czytać wolumenu jak produkcji.',
  },
  REFERENCYJNE_TEST: {
    tekst: 'realne referencyjne (TEST)',
    barwa: 'var(--seria-3)',
    tlo: 'var(--sukces-tlo)',
    opis: 'Realna oferta i słowniki środowiska TEST. To NIE jest sprzedaż.',
  },
  ZA_MALO_DANYCH: {
    tekst: 'za mało danych',
    barwa: 'var(--neutralny)',
    tlo: 'var(--neutralny-tlo)',
    opis: 'Dane są, ale jest ich za mało, żeby oprzeć na nich wniosek.',
  },
};

/** Odwzorowanie klasyfikacji dowodu na znacznik. Jedno miejsce, żeby ekrany nie robiły tego same. */
export function stanZKlasyfikacji(k: KlasyfikacjaZrodla): StanZrodla {
  switch (k) {
    case 'TEST_REALNE_REFERENCYJNE':
      return 'REFERENCYJNE_TEST';
    case 'TEST_KONTROLOWANE':
      return 'KONTROLOWANE';
    case 'REALNE_TEST':
    case 'PRODUKCJA':
      return 'BIEZACE';
    case 'BRAK_ZRODLA':
      return 'ZABLOKOWANE';
    default:
      return 'NIEUSTALONE';
  }
}

export function ZnacznikZrodla({ stan, kompaktowy = false }: { stan: StanZrodla; kompaktowy?: boolean }) {
  const z = ZNACZNIK[stan];
  return (
    <Box
      component="span"
      title={z.opis}
      aria-label={`${z.tekst}. ${z.opis}`}
      role="img"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: kompaktowy ? 0.75 : 1.25,
        py: kompaktowy ? '1px' : '3px',
        borderRadius: 'var(--r-pigulka)',
        background: z.tlo,
        color: z.barwa,
        fontSize: kompaktowy ? 'var(--st-mikro)' : 'var(--st-drobny)',
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}
    >
      {/* Kropka NIE jest jedynym nośnikiem — tekst obok mówi to samo. */}
      <Box component="span" aria-hidden sx={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor', flex: 'none' }} />
      {z.tekst}
    </Box>
  );
}
